import { useState, useEffect, useRef, useCallback } from 'react';
import { ChapterWorldMap } from '../components/dashboard/ChapterWorldMap';
import QRCode from 'qrcode';

const API_BASE = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:3000');
const POLL_INTERVAL = 30_000; // 30s auto-refresh
const SPOTLIGHT_INTERVAL = 15_000; // 15s rotation
const WISDOM_INTERVAL = 20_000; // 20s rotation

// ── Types ────────────────────────────────────────────────────────────────────

interface EventSummary {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  venueName: string;
}

interface WeekendEvent {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  date: string;
  venueName: string;
  venue?: { name: string; address: string; city: string; state: string; capacity: number; parkingInfo?: string } | null;
  guestPassSlug?: string;
  isPast: boolean;
  isCurrent: boolean;
  agendaItems: { id: string; title: string; startTime: string; endTime: string; speaker?: string }[];
  stats?: { registrations: number; vendors: number; agendaItems: number };
  subcommittees?: { id: string; name: string }[];
}

interface SpotlightSponsor {
  id: string;
  name: string;
  tier: string;
}

interface SpotlightChapter {
  id: string;
  name: string;
  city: string;
  country: string;
  president: string;
}

interface WisdomEntry {
  id: string;
  title: string;
  translation: string;
  interpretation: string;
  collection: string;
}

interface ReunionWeekendData {
  area: string;
  currentEvent: EventSummary | null;
  nextEvent: EventSummary | null;
  spotlight: {
    sponsors: SpotlightSponsor[];
    chapters: SpotlightChapter[];
    venue: { name: string; eventName: string } | null;
    latestAnnouncement: { id: string; title: string; body: string; createdAt: string } | null;
  };
  todaySchedule: EventSummary[];
  weekendSchedule?: WeekendEvent[];
  recentCheckIns: { name: string; chapter: string; checkedInAt: string }[];
  liveStats: {
    totalAttendees: number;
    totalCheckedIn: number;
    chaptersRepresented: number;
    tableFillPercent: number;
  };
  krioWisdom: WisdomEntry | null;
  allWisdom: WisdomEntry[];
  orgChart?: { id: string; name: string; chair: string; memberCount: number; members: { name: string; role: string }[] }[];
  mediaItems?: { id: string; imageUrl: string; caption?: string }[];
}

// ── Utility Functions ────────────────────────────────────────────────────────

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number, minutes: number;
  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function getCountdown(targetIso: string): string {
  const target = new Date(targetIso).getTime();
  const diff = target - Date.now();
  if (diff <= 0) return 'Starting now';
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

// ── Component ────────────────────────────────────────────────────────────────

type SpotlightSlide = 'sponsors' | 'georgia' | 'chapters' | 'venue' | 'marketplace' | 'announcement' | 'photos';
const SPOTLIGHT_SLIDES: SpotlightSlide[] = ['sponsors', 'georgia', 'chapters', 'venue', 'marketplace', 'announcement', 'photos'];

const SCHEDULE_ROTATION_INTERVAL = 15_000; // 15s per event

export function ReunionWeekendDashboardPage() {
  const [data, setData] = useState<ReunionWeekendData | null>(null);
  const [countdown, setCountdown] = useState('');
  const [spotlightIndex, setSpotlightIndex] = useState(0);
  const [wisdomIndex, setWisdomIndex] = useState(0);
  const [scheduleEventIndex, setScheduleEventIndex] = useState(0);
  const [isOffline, setIsOffline] = useState(false);
  const intervalRef = useRef<number | null>(null);

  // ── Data Fetching ──────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/public/dashboard/reunion-weekend`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: ReunionWeekendData = await res.json();
      setData(json);
      setIsOffline(false);
    } catch {
      setIsOffline(true);
    }
  }, []);

  useEffect(() => {
    fetchData();
    intervalRef.current = window.setInterval(fetchData, POLL_INTERVAL);
    return () => { if (intervalRef.current) window.clearInterval(intervalRef.current); };
  }, [fetchData]);

  // ── Countdown Timer (updates every second) ─────────────────
  useEffect(() => {
    const target = data?.nextEvent?.startTime || data?.currentEvent?.endTime;
    if (!target) { setCountdown(''); return; }
    const tick = () => setCountdown(getCountdown(target));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [data?.nextEvent?.startTime, data?.currentEvent?.endTime]);

  // ── Spotlight Rotation (georgia slide stays for all committees) ──
  useEffect(() => {
    const committeeCount = data?.orgChart?.length || 1;
    let timeout: number;
    const advance = () => {
      setSpotlightIndex((prev) => {
        const next = (prev + 1) % SPOTLIGHT_SLIDES.length;
        // Schedule next advance — georgia slide gets extra time for all committees
        const nextDuration = SPOTLIGHT_SLIDES[next] === 'georgia'
          ? committeeCount * 5000
          : SPOTLIGHT_INTERVAL;
        timeout = window.setTimeout(advance, nextDuration);
        return next;
      });
    };
    // Start first timer based on current slide
    const currentDuration = SPOTLIGHT_SLIDES[spotlightIndex] === 'georgia'
      ? committeeCount * 5000
      : SPOTLIGHT_INTERVAL;
    timeout = window.setTimeout(advance, currentDuration);
    return () => window.clearTimeout(timeout);
  }, [data?.orgChart?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Wisdom Rotation ────────────────────────────────────────
  useEffect(() => {
    if (!data?.allWisdom?.length) return;
    const id = window.setInterval(() => {
      setWisdomIndex((i) => (i + 1) % (data.allWisdom.length || 1));
    }, WISDOM_INTERVAL);
    return () => window.clearInterval(id);
  }, [data?.allWisdom?.length]);

  // ── Schedule Event Rotation ────────────────────────────────
  const activeEvents = (data?.weekendSchedule || []).filter((e) => !e.isPast);
  useEffect(() => {
    if (activeEvents.length <= 1) return;
    const id = window.setInterval(() => {
      setScheduleEventIndex((i) => (i + 1) % activeEvents.length);
    }, SCHEDULE_ROTATION_INTERVAL);
    return () => window.clearInterval(id);
  }, [activeEvents.length]);

  // ── Hide cursor after 3s inactivity + lock body overflow ──
  useEffect(() => {
    document.body.classList.add('reunion-dashboard-active');
    let timeout: number;
    const show = () => {
      document.body.style.cursor = 'default';
      clearTimeout(timeout);
      timeout = window.setTimeout(() => { document.body.style.cursor = 'none'; }, 3000);
    };
    show();
    window.addEventListener('mousemove', show);
    return () => {
      window.removeEventListener('mousemove', show);
      document.body.style.cursor = 'default';
      document.body.classList.remove('reunion-dashboard-active');
    };
  }, []);

  const currentWisdom = data?.allWisdom?.[wisdomIndex] || data?.krioWisdom;

  return (
    <div style={styles.root}>
      {/* KDU Logo — bottom right */}
      {(() => {
        const logoMedia = (data?.mediaItems || []).find(
          (m) => m.caption && m.caption.toLowerCase().startsWith('logo:')
        );
        const logoSrc = logoMedia
          ? (logoMedia.imageUrl.startsWith('http') ? logoMedia.imageUrl : API_BASE + logoMedia.imageUrl)
          : '/images/KDUGaLogo.png';
        return <img src={logoSrc} alt="KDU Georgia 2026" style={{ position: 'fixed', bottom: 16, right: 20, height: '90px', opacity: 0.85, pointerEvents: 'none', zIndex: 2 }} />;
      })()}

      {/* Offline indicator */}
      {isOffline && <div style={styles.offline}>Reconnecting...</div>}

      {/* Two-column layout: main content + schedule sidebar */}
      <div style={styles.outerLayout}>
        {/* Left: Main Content Grid */}
        <div style={styles.mainColumn}>
          <div style={styles.grid}>
            {/* Section 1: Current/Next Event */}
            <div style={{ ...styles.card, ...styles.currentEvent }}>
              <div style={styles.sectionLabel}>NOW</div>
              {data?.currentEvent ? (
                <>
                  <h2 style={styles.eventName}>{data.currentEvent.name}</h2>
                  <p style={styles.eventVenue}>{data.currentEvent.venueName}</p>
                  <p style={styles.eventTime}>
                    {formatTime(data.currentEvent.startTime)} – {formatTime(data.currentEvent.endTime)}
                  </p>
                </>
              ) : (
                <p style={styles.noEvent}>No event in progress</p>
              )}
              {data?.nextEvent && (
                <div style={styles.nextEventBox}>
                  <span style={styles.upNextLabel}>UP NEXT</span>
                  <span style={styles.nextEventName}>{data.nextEvent.name}</span>
                  {countdown && <span style={styles.countdown}>{countdown}</span>}
                </div>
              )}
            </div>

            {/* Section 2: Rotating Spotlight */}
            <div style={{ ...styles.card, ...styles.spotlight }}>
              {/* Venue info at top — synced with current event */}
              {(() => {
                const evt = activeEvents.length > 0 ? activeEvents[scheduleEventIndex % activeEvents.length] : null;
                return evt?.venue ? (
                  <div style={{ marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' as const }}>
                    <span style={{ fontSize: '0.8rem', color: '#7feba8', fontWeight: 600 }}>📍 {evt.venue.name}</span>
                    <span style={{ fontSize: '0.7rem', color: '#999', marginLeft: 8 }}>{evt.venue.city}, {evt.venue.state}</span>
                  </div>
                ) : null;
              })()}

              <div style={styles.sectionLabel}>
                {SPOTLIGHT_SLIDES[spotlightIndex].toUpperCase().replace('_', ' ')}
              </div>
              <SpotlightContent
                slide={SPOTLIGHT_SLIDES[spotlightIndex]}
                data={data}
                activeEvent={activeEvents.length > 0 ? activeEvents[scheduleEventIndex % activeEvents.length] : undefined}
              />
              {/* Dots indicator */}
              <div style={styles.dots}>
                {SPOTLIGHT_SLIDES.map((_, i) => (
                  <span key={i} style={i === spotlightIndex ? styles.dotActive : styles.dot} />
                ))}
              </div>

              {/* Events timeline pills at bottom */}
              {activeEvents.length > 0 && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' as const }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '5px', justifyContent: 'center' }}>
                    {activeEvents.map((evt, i) => {
                      const isCurrent = i === (scheduleEventIndex % activeEvents.length);
                      return (
                        <span key={evt.id} style={{ fontSize: '0.68rem', padding: '4px 10px', background: isCurrent ? 'rgba(127,235,168,0.2)' : 'rgba(255,255,255,0.05)', border: isCurrent ? '1px solid #7feba8' : '1px solid rgba(255,255,255,0.1)', borderRadius: 20, color: isCurrent ? '#7feba8' : '#aaa', fontWeight: isCurrent ? 700 : 400 }}>
                          {evt.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Section 5: Live Stats — contextual to current/displayed event */}
            <div style={{ ...styles.card, ...styles.stats }}>
              <div style={styles.sectionLabel}>
                {activeEvents.length > 0
                  ? `STATS — ${activeEvents[scheduleEventIndex % activeEvents.length]?.name || 'EVENT'}`
                  : 'REUNION STATS'}
              </div>
              <div style={styles.statsGrid}>
                {activeEvents.length > 0 ? (
                  (() => {
                    const evt = activeEvents[scheduleEventIndex % activeEvents.length];
                    return (
                      <>
                        <StatCard label="Registered" value={evt.stats?.registrations ?? 0} icon="🎟️" />
                        <StatCard label="Vendors" value={evt.stats?.vendors ?? 0} icon="🏪" />
                        <StatCard label="Agenda Items" value={evt.stats?.agendaItems ?? 0} icon="📋" />
                        <StatCard label="Chapters" value={data?.liveStats?.chaptersRepresented ?? 0} icon="🌍" />
                      </>
                    );
                  })()
                ) : (
                  <>
                    <StatCard label="Attendees" value={data?.liveStats?.totalAttendees ?? 0} icon="👥" />
                    <StatCard label="Checked In" value={data?.liveStats?.totalCheckedIn ?? 0} icon="✅" />
                    <StatCard label="Chapters" value={data?.liveStats?.chaptersRepresented ?? 0} icon="🌍" />
                    <StatCard label="Tables Filled" value={`${data?.liveStats?.tableFillPercent ?? 0}%`} icon="🪑" />
                  </>
                )}
              </div>
              {/* QR Code pill below stats */}
              {(() => {
                const currentSlide = SPOTLIGHT_SLIDES[spotlightIndex];
                const isSponsors = currentSlide === 'sponsors';
                const activeEvt = activeEvents.length > 0 ? activeEvents[scheduleEventIndex % activeEvents.length] : null;
                // During sponsors: show kduhub.com QR; otherwise: show event pass QR
                if (isSponsors) {
                  return (
                    <div style={styles.qrPill}>
                      <EventQRCode slug="" eventName="KDU Hub" />
                    </div>
                  );
                }
                if (activeEvt?.guestPassSlug) {
                  return (
                    <div style={styles.qrPill}>
                      <EventQRCode slug={activeEvt.guestPassSlug} eventName={activeEvt.name} />
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Section 4: Welcome Ticker */}
            <div style={{ ...styles.card, ...styles.ticker }}>
              <div style={styles.sectionLabel}>WELCOME</div>
              <div style={styles.marqueeContainer}>
                <div style={styles.marquee}>
                  {data?.recentCheckIns?.length ? (
                    data.recentCheckIns.map((g, i) => (
                      <span key={i} style={styles.marqueeItem}>
                        {g.name}{g.chapter ? ` (${g.chapter})` : ''}
                        <span style={styles.marqueeSep}> ✦ </span>
                      </span>
                    ))
                  ) : (
                    <span style={styles.marqueeItem}>Awaiting first check-in...</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 6: Krio Wisdom — slim bottom bar */}
          {currentWisdom && (
            <div style={styles.wisdom}>
              <span style={styles.wisdomIcon}>🇸🇱</span>
              <span style={styles.wisdomTitle}>{currentWisdom.title}</span>
              <span style={styles.wisdomSep}>—</span>
              <span style={styles.wisdomTranslation}>{currentWisdom.translation}</span>
            </div>
          )}
        </div>

        {/* Right: Schedule Sidebar (independent scroll) */}
        <div style={styles.scheduleColumn}>
          <div style={{ ...styles.card, ...styles.schedule }}>
            <div style={styles.sectionLabel}>{SPOTLIGHT_SLIDES[spotlightIndex] === 'sponsors' ? 'REUNION EVENTS' : 'SCHEDULE'}</div>
            {SPOTLIGHT_SLIDES[spotlightIndex] === 'sponsors' ? (
              /* During sponsors: show all events as static timeline pills */
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                {activeEvents.map((evt) => (
                  <div key={evt.id} style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(127,235,168,0.15)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#7feba8', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 600, display: 'block' }}>{evt.name}</span>
                      <span style={{ fontSize: '0.68rem', color: '#aedfc4' }}>{formatTime(evt.startTime)} · {evt.venueName}</span>
                    </div>
                  </div>
                ))}
                {activeEvents.length === 0 && <p style={{ color: '#888', fontSize: '0.8rem' }}>No upcoming events</p>}
              </div>
            ) : (
            activeEvents.length > 0 ? (
              (() => {
                const evt = activeEvents[scheduleEventIndex % activeEvents.length];
                return (
                  <div>
                    <div style={styles.scheduleHeader}>
                      <h3 style={styles.scheduleEventName}>{evt.name}</h3>
                      <span style={styles.scheduleVenue}>{evt.venueName}</span>
                      {evt.venue && (
                        <span style={styles.scheduleAddress}>
                          {evt.venue.address}, {evt.venue.city}, {evt.venue.state}
                          {evt.venue.capacity > 0 && ` · Capacity: ${evt.venue.capacity}`}
                        </span>
                      )}
                      <span style={styles.scheduleTime}>
                        {formatTime(evt.startTime)} – {formatTime(evt.endTime)}
                      </span>
                      {evt.isCurrent && <span style={styles.liveBadge}>LIVE</span>}
                    </div>
                    {evt.agendaItems.length > 0 ? (
                      <div style={styles.timeline}>
                        {evt.agendaItems.map((item) => (
                          <div key={item.id} style={styles.timelineItem}>
                            <span style={styles.timelineTime}>{formatTime(item.startTime)}</span>
                            <span style={styles.timelineDot}>
                              <span style={styles.timelineDotInner} />
                            </span>
                            <div style={{ display: 'flex', flexDirection: 'column' as const }}>
                              <span style={styles.timelineName}>{item.title}</span>
                              {item.speaker && <span style={styles.timelineSpeaker}>{item.speaker}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ ...styles.noEvent, marginTop: 12 }}>Agenda details coming soon</p>
                    )}
                    {/* Event subcommittees */}
                    {evt.subcommittees && evt.subcommittees.length > 0 && (
                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <p style={{ margin: '0 0 6px', fontSize: '0.65rem', color: '#7feba8', fontWeight: 700, letterSpacing: '0.08em' }}>COMMITTEES</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '4px' }}>
                          {evt.subcommittees.map((sc) => (
                            <span key={sc.id} style={{ fontSize: '0.7rem', padding: '3px 8px', background: 'rgba(127,235,168,0.1)', borderRadius: 4, color: '#aedfc4' }}>{sc.name}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Event counter dots */}
                    {activeEvents.length > 1 && (
                      <div style={{ ...styles.dots, marginTop: 12 }}>
                        {activeEvents.map((_, i) => (
                          <span key={i} style={i === (scheduleEventIndex % activeEvents.length) ? styles.dotActive : styles.dot} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <div style={{ textAlign: 'center' as const, padding: '30px 10px' }}>
                <p style={{ fontSize: '1.3rem', marginBottom: 8 }}>🎉</p>
                <p style={{ color: '#7feba8', fontSize: '1rem', fontWeight: 600, margin: '0 0 6px 0' }}>
                  Thank you for an amazing reunion!
                </p>
                <p style={{ color: '#aaa', fontSize: '0.85rem', margin: 0 }}>
                  All events have concluded. See you next time!
                </p>
              </div>
            )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-Components ───────────────────────────────────────────────────────────

function SpotlightContent({ slide, data, activeEvent }: { slide: SpotlightSlide; data: ReunionWeekendData | null; activeEvent?: WeekendEvent }) {
  if (!data) return <p style={{ color: '#ccc' }}>Loading...</p>;

  switch (slide) {
    case 'sponsors': {
      // Use media items with caption starting with "Sponsor:" as logos
      const sponsorMedia = (data.mediaItems || []).filter(
        (m) => m.caption && m.caption.toLowerCase().startsWith('sponsor:')
      );
      if (sponsorMedia.length > 0) {
        // Show one sponsor at a time — rotate based on spotlight timer
        const sponsorIdx = Math.floor(Date.now() / 10000) % sponsorMedia.length;
        const current = sponsorMedia[sponsorIdx];
        const sponsorName = current.caption!.replace(/^sponsor:\s*/i, '');
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', padding: '20px', gap: '16px' }}>
            <img
              src={current.imageUrl.startsWith('http') ? current.imageUrl : API_BASE + current.imageUrl}
              alt={sponsorName}
              style={{ maxHeight: '700px', maxWidth: '100%', objectFit: 'contain', borderRadius: 12, border: '3px solid rgba(127,235,168,0.4)', padding: '12px', background: 'rgba(255,255,255,0.03)' }}
            />
            <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#fff', textAlign: 'center' as const }}>{sponsorName}</p>
            {sponsorMedia.length > 1 && (
              <p style={{ margin: 0, fontSize: '0.7rem', color: '#7feba8' }}>
                {sponsorIdx + 1} of {sponsorMedia.length} sponsors
              </p>
            )}
          </div>
        );
      }
      // Fallback to text-based sponsor chips from DB
      return (
        <div style={styles.spotlightGrid}>
          {data.spotlight.sponsors.length ? (
            data.spotlight.sponsors.slice(0, 8).map((s) => (
              <div key={s.id} style={styles.sponsorChip}>
                <span style={styles.sponsorTier}>{s.tier}</span>
                <span style={styles.sponsorName}>{s.name}</span>
              </div>
            ))
          ) : (
            <p style={{ color: '#aaa' }}>Sponsors coming soon</p>
          )}
        </div>
      );
    }
    case 'chapters': {
      // If there's an uploaded "Chapter:" image, show it prominently
      const chapterImg = (data.mediaItems || []).find(
        (m) => m.caption && m.caption.toLowerCase().startsWith('chapters:')
      );
      if (chapterImg) {
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            <img
              src={chapterImg.imageUrl.startsWith('http') ? chapterImg.imageUrl : API_BASE + chapterImg.imageUrl}
              alt={chapterImg.caption || 'KDU Chapters'}
              style={{ maxHeight: '700px', maxWidth: '95%', objectFit: 'contain', borderRadius: 10 }}
            />
            {chapterImg.caption && (
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#aedfc4' }}>{chapterImg.caption.replace(/^chapters:\s*/i, '')}</p>
            )}
          </div>
        );
      }
      // Fallback: large readable text list of chapters
      const chapters = data.spotlight.chapters;
      return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '1rem', fontWeight: 700, color: '#7feba8', letterSpacing: '0.08em' }}>
            {chapters.length} CHAPTERS WORLDWIDE
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '10px', justifyContent: 'center' }}>
            {chapters.map((c) => (
              <div key={c.id} style={{ padding: '8px 14px', background: 'rgba(127,235,168,0.08)', borderRadius: 8, border: '1px solid rgba(127,235,168,0.15)' }}>
                <span style={{ fontSize: '0.95rem', color: '#fff', fontWeight: 600 }}>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case 'georgia': {
      // Show uploaded "Logo:" or "Chapter:" media + rotating subcommittee org chart
      const chapterMedia = (data.mediaItems || []).filter(
        (m) => m.caption && (m.caption.toLowerCase().startsWith('logo:') || m.caption.toLowerCase().startsWith('chapter:'))
      );
      const committees = data.orgChart || [];
      const committeeIdx = committees.length > 0 ? Math.floor(Date.now() / 5000) % committees.length : 0;
      const currentCommittee = committees[committeeIdx];

      return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: '10px', overflowY: 'auto' as const }}>
          {/* Logo/Image at top */}
          {chapterMedia.length > 0 && (
            <img
              src={chapterMedia[0].imageUrl.startsWith('http') ? chapterMedia[0].imageUrl : API_BASE + chapterMedia[0].imageUrl}
              alt={chapterMedia[0].caption || 'Georgia Chapter'}
              style={{ maxHeight: '120px', maxWidth: '80%', objectFit: 'contain', borderRadius: 8 }}
            />
          )}

          {/* Current subcommittee detail card */}
          {currentCommittee ? (
            <div style={{ width: '100%', flex: 1 }}>
              <div style={{ textAlign: 'center' as const, marginBottom: 8 }}>
                <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#7feba8' }}>{currentCommittee.name}</p>
                <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: '#aaa' }}>{currentCommittee.memberCount} member{currentCommittee.memberCount !== 1 ? 's' : ''}</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                {currentCommittee.members.map((m, i) => (
                  <div key={i} style={{ padding: '6px 10px', background: m.role === 'Chair' ? 'rgba(127,235,168,0.12)' : 'rgba(255,255,255,0.04)', borderRadius: 6, borderLeft: m.role === 'Chair' ? '3px solid #7feba8' : '3px solid rgba(255,255,255,0.1)' }}>
                    <span style={{ fontSize: '0.78rem', color: '#fff', fontWeight: m.role === 'Chair' ? 700 : 400 }}>{m.name}</span>
                    <br />
                    <span style={{ fontSize: '0.6rem', color: m.role === 'Chair' ? '#7feba8' : '#888' }}>{m.role}</span>
                  </div>
                ))}
              </div>
              {/* Counter */}
              {committees.length > 1 && (
                <p style={{ textAlign: 'center' as const, margin: '8px 0 0', fontSize: '0.65rem', color: '#666' }}>
                  {committeeIdx + 1} of {committees.length} committees
                </p>
              )}
            </div>
          ) : (
            <p style={{ color: '#aaa' }}>No subcommittees configured</p>
          )}
        </div>
      );
    }
    case 'venue': {
      // Sync with the schedule rotation — show venue for the currently displayed event
      const venue = activeEvent?.venue;
      const evtName = activeEvent?.name;
      return venue ? (
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <p style={{ fontSize: '1.3rem', color: '#7feba8', marginBottom: 6, fontWeight: 700 }}>📍 {venue.name}</p>
          <p style={{ fontSize: '0.9rem', color: '#ddd', marginBottom: 4 }}>{venue.address}</p>
          <p style={{ fontSize: '0.85rem', color: '#aedfc4', marginBottom: 8 }}>{venue.city}, {venue.state}</p>
          {venue.capacity > 0 && (
            <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: 4 }}>Capacity: {venue.capacity}</p>
          )}
          {venue.parkingInfo && (
            <p style={{ fontSize: '0.75rem', color: '#888', marginTop: 8, lineHeight: 1.4 }}>
              🅿️ {venue.parkingInfo.slice(0, 120)}{venue.parkingInfo.length > 120 ? '...' : ''}
            </p>
          )}
          <p style={{ fontSize: '0.75rem', color: '#666', marginTop: 10 }}>
            Hosting: {evtName}
          </p>
        </div>
      ) : data.spotlight.venue ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ fontSize: '1.4rem', color: '#7feba8', marginBottom: 8 }}>{data.spotlight.venue.name}</p>
          <p style={{ fontSize: '1rem', color: '#ccc' }}>Hosting: {data.spotlight.venue.eventName}</p>
        </div>
      ) : (
        <p style={{ color: '#aaa' }}>Venue info unavailable</p>
      );
    }
    case 'marketplace': {
      // Show uploaded "Booklet:" or "Marketplace:" media — like sponsor card with border
      const marketMedia = (data.mediaItems || []).find(
        (m) => m.caption && (m.caption.toLowerCase().startsWith('booklet:') || m.caption.toLowerCase().startsWith('marketplace:'))
      );
      return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', padding: '16px', gap: '14px' }}>
          {marketMedia ? (
            <img
              src={marketMedia.imageUrl.startsWith('http') ? marketMedia.imageUrl : API_BASE + marketMedia.imageUrl}
              alt="Weekend Booklet"
              style={{ maxHeight: '700px', maxWidth: '100%', objectFit: 'contain', borderRadius: 12, border: '3px solid rgba(127,235,168,0.4)', padding: '12px', background: 'rgba(255,255,255,0.03)' }}
            />
          ) : (
            <p style={{ color: '#aaa' }}>Upload a media item with caption "Booklet: ..." to display here</p>
          )}
          <div style={{ textAlign: 'center' as const }}>
            <p style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#fff' }}>Reunion Registration Desk</p>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#aedfc4' }}>KDU Marketplace</p>
            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#999' }}>Hilton Atlanta, NE Hotel</p>
          </div>
        </div>
      );
    }
    case 'announcement':
      return data.spotlight.latestAnnouncement ? (
        <div style={{ padding: '12px 16px' }}>
          <p style={{ fontSize: '1.1rem', color: '#7feba8', marginBottom: 6 }}>
            {data.spotlight.latestAnnouncement.title}
          </p>
          <p style={{ fontSize: '0.9rem', color: '#ddd', lineHeight: 1.5 }}>
            {data.spotlight.latestAnnouncement.body.slice(0, 200)}
            {data.spotlight.latestAnnouncement.body.length > 200 ? '...' : ''}
          </p>
        </div>
      ) : (
        <p style={{ color: '#aaa' }}>No announcements yet</p>
      );
    case 'photos':
      // Filter out sponsor images (those go on sponsors slide)
      const nonSponsorMedia = (data.mediaItems || []).filter(
        (m) => !m.caption || !m.caption.toLowerCase().startsWith('sponsor:')
      );
      if (nonSponsorMedia.length > 0) {
        // Show one photo at a time, full size, rotating
        const photoIdx = Math.floor(Date.now() / 6000) % nonSponsorMedia.length;
        const photo = nonSponsorMedia[photoIdx];
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'flex-start', gap: '10px', paddingTop: '4px' }}>
            <img
              src={photo.imageUrl.startsWith('http') ? photo.imageUrl : API_BASE + photo.imageUrl}
              alt={photo.caption || 'Event photo'}
              style={{ maxHeight: '700px', maxWidth: '95%', objectFit: 'contain', borderRadius: 10 }}
            />
            {photo.caption && (
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#ccc', textAlign: 'center' as const }}>{photo.caption}</p>
            )}
            {nonSponsorMedia.length > 1 && (
              <p style={{ margin: 0, fontSize: '0.65rem', color: '#666' }}>{photoIdx + 1} of {nonSponsorMedia.length}</p>
            )}
          </div>
        );
      }
      return (
        <div style={{ padding: '12px 0' }}>
          {data.funContent?.length ? (
            data.funContent.slice(0, 3).map((item) => (
              <div key={item.id} style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: 8 }}>
                <p style={{ fontSize: '0.9rem', color: '#fff', margin: '0 0 4px 0', lineHeight: 1.4 }}>{item.text}</p>
                {item.attribution && <p style={{ fontSize: '0.7rem', color: '#aedfc4', margin: 0 }}>— {item.attribution}</p>}
              </div>
            ))
          ) : (
            <p style={{ color: '#aaa', textAlign: 'center', paddingTop: 30 }}>Content coming soon</p>
          )}
        </div>
      );
  }
}

function StatCard({ label, value, icon }: { label: string; value: number | string; icon: string }) {
  return (
    <div style={styles.statCard}>
      <span style={styles.statIcon}>{icon}</span>
      <span style={styles.statValue}>{value}</span>
      <span style={styles.statLabel}>{label}</span>
    </div>
  );
}

function EventQRCode({ slug, eventName }: { slug: string; eventName: string }) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  // If slug is empty, link to kduhub.com; otherwise event pass
  const passUrl = slug ? `${window.location.origin}/pass/${slug}` : window.location.origin;
  const label = slug ? 'SCAN FOR EVENT INFO' : 'VISIT KDU HUB';

  useEffect(() => {
    QRCode.toDataURL(passUrl, {
      width: 400,
      margin: 2,
      color: { dark: '#0f2d1f', light: '#ffffff' },
    }).then(setQrDataUrl).catch(() => {});
  }, [passUrl]);

  if (!qrDataUrl) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: '6px', padding: '12px 0 8px' }}>
      <img src={qrDataUrl} alt={`QR code for ${eventName}`} style={{ width: 140, height: 140, borderRadius: 10, boxShadow: '0 0 16px rgba(127,235,168,0.25)' }} />
      <p style={{ margin: 0, fontSize: '0.8rem', color: '#7feba8', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' as const }}>{label}</p>
      <p style={{ margin: 0, fontSize: '0.7rem', color: '#ccc' }}>{eventName}</p>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const GLASS = {
  background: 'rgba(255,255,255,0.06)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '16px',
} as React.CSSProperties;

const styles: Record<string, React.CSSProperties> = {
  root: {
    height: '100vh',
    width: '100vw',
    background: 'linear-gradient(135deg, #0f2d1f 0%, #1a4a2e 50%, #0f2d1f 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#ffffff',
    padding: '20px',
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  watermark: {
    position: 'fixed',
    bottom: '20px',
    right: '30px',
    fontSize: '4rem',
    fontWeight: 900,
    color: 'rgba(255,255,255,0.04)',
    letterSpacing: '0.2em',
    userSelect: 'none',
    pointerEvents: 'none',
    zIndex: 0,
  },
  offline: {
    position: 'fixed',
    top: 12,
    right: 12,
    background: 'rgba(220,50,50,0.8)',
    color: '#fff',
    padding: '6px 14px',
    borderRadius: 20,
    fontSize: '0.8rem',
    zIndex: 100,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.4fr',
    gridTemplateRows: 'auto 1fr auto',
    gap: '16px',
    flex: 1,
  },
  outerLayout: {
    display: 'flex',
    gap: '16px',
    flex: 1,
    position: 'relative',
    zIndex: 1,
    maxWidth: '1800px',
    margin: '0 auto',
    width: '100%',
  },
  mainColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: 0,
  },
  scheduleColumn: {
    width: '400px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    ...GLASS,
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
  },
  sectionLabel: {
    fontSize: '0.9rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    color: '#7feba8',
    marginBottom: '12px',
    textTransform: 'uppercase' as const,
  },
  // Section 1: Current Event
  currentEvent: {
    gridColumn: '1',
    gridRow: '1',
  },
  eventName: {
    fontSize: '1.6rem',
    fontWeight: 700,
    margin: '0 0 4px 0',
    lineHeight: 1.2,
  },
  eventVenue: {
    fontSize: '0.95rem',
    color: '#aedfc4',
    margin: '0 0 4px 0',
  },
  eventTime: {
    fontSize: '0.9rem',
    color: '#ccc',
    margin: 0,
  },
  noEvent: {
    color: '#888',
    fontStyle: 'italic',
    margin: 0,
  },
  nextEventBox: {
    marginTop: '16px',
    padding: '10px 14px',
    background: 'rgba(127,235,168,0.08)',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap' as const,
  },
  upNextLabel: {
    fontSize: '0.65rem',
    fontWeight: 700,
    background: '#7feba8',
    color: '#0f2d1f',
    padding: '2px 8px',
    borderRadius: 4,
    letterSpacing: '0.1em',
  },
  nextEventName: {
    fontSize: '0.95rem',
    color: '#fff',
    flex: 1,
  },
  countdown: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#7feba8',
    fontVariantNumeric: 'tabular-nums',
  },
  // Section 2: Spotlight
  spotlight: {
    gridColumn: '2',
    gridRow: '1 / 3',
    minHeight: '280px',
    display: 'flex',
    flexDirection: 'column',
  },
  dots: {
    display: 'flex',
    gap: 6,
    justifyContent: 'center',
    marginTop: 'auto',
    paddingTop: 12,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.2)',
  },
  dotActive: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#7feba8',
  },
  spotlightGrid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    padding: '8px 0',
  },
  sponsorChip: {
    background: 'rgba(255,255,255,0.08)',
    borderRadius: 8,
    padding: '8px 12px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  sponsorTier: {
    fontSize: '0.6rem',
    color: '#7feba8',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
  },
  sponsorName: {
    fontSize: '0.85rem',
    color: '#fff',
  },
  chapterRoll: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
    padding: '4px 0',
  },
  chapterChip: {
    background: 'rgba(127,235,168,0.1)',
    borderRadius: 6,
    padding: '6px 10px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1,
  },
  chapterName: {
    fontSize: '0.8rem',
    color: '#fff',
    fontWeight: 600,
  },
  chapterLocation: {
    fontSize: '0.65rem',
    color: '#aedfc4',
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    height: '200px',
  },
  photoItem: {
    borderRadius: 8,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative' as const,
    overflow: 'hidden',
  },
  photoCaption: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    padding: '4px 8px',
    background: 'rgba(0,0,0,0.6)',
    fontSize: '0.7rem',
    color: '#fff',
  },
  // Section 3: Schedule (independent right sidebar)
  schedule: {
    flex: 1,
    overflowY: 'auto' as const,
  },
  scheduleHeader: {
    marginBottom: '12px',
    paddingBottom: '10px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  scheduleEventName: {
    fontSize: '1.1rem',
    fontWeight: 700,
    margin: '0 0 4px 0',
    color: '#fff',
  },
  scheduleVenue: {
    display: 'block',
    fontSize: '0.8rem',
    color: '#aedfc4',
    marginBottom: 2,
  },
  scheduleTime: {
    display: 'block',
    fontSize: '0.75rem',
    color: '#999',
  },
  scheduleAddress: {
    display: 'block',
    fontSize: '0.72rem',
    color: '#8db8a0',
    marginBottom: 2,
  },
  liveBadge: {
    display: 'inline-block',
    marginTop: 6,
    fontSize: '0.6rem',
    fontWeight: 700,
    background: '#ef4444',
    color: '#fff',
    padding: '2px 8px',
    borderRadius: 4,
    letterSpacing: '0.1em',
    animation: 'pulse-live 2s infinite',
  },
  timelineSpeaker: {
    fontSize: '0.7rem',
    color: '#aedfc4',
    fontStyle: 'italic',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 0,
  },
  timelineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 0',
    borderLeft: '2px solid rgba(255,255,255,0.1)',
    paddingLeft: '12px',
    marginLeft: '2px',
  },
  timelineItemActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 0',
    borderLeft: '2px solid #7feba8',
    paddingLeft: '12px',
    marginLeft: '2px',
    background: 'rgba(127,235,168,0.06)',
    borderRadius: '0 8px 8px 0',
  },
  timelineTime: {
    fontSize: '0.75rem',
    color: '#aedfc4',
    width: '65px',
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
  },
  timelineDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  timelineDotInner: {
    width: 4,
    height: 4,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.4)',
  },
  timelineDotInnerActive: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#7feba8',
    boxShadow: '0 0 6px #7feba8',
  },
  timelineName: {
    fontSize: '0.85rem',
    color: '#fff',
  },
  // Section 4: Ticker
  ticker: {
    gridColumn: '1 / 3',
    gridRow: '3',
    padding: '14px 20px',
    overflow: 'hidden',
  },
  marqueeContainer: {
    overflow: 'hidden',
    whiteSpace: 'nowrap' as const,
  },
  marquee: {
    display: 'inline-block',
    animation: 'marquee-scroll 40s linear infinite',
    whiteSpace: 'nowrap' as const,
  },
  marqueeItem: {
    fontSize: '1rem',
    color: '#fff',
    marginRight: '8px',
  },
  marqueeSep: {
    color: '#7feba8',
    margin: '0 4px',
  },
  // Section 5: Live Stats
  stats: {
    gridColumn: '1',
    gridRow: '2',
  },
  // QR Code pill (inside stats card)
  qrPill: {
    marginTop: '16px',
    paddingTop: '14px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    justifyContent: 'center',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  statCard: {
    background: 'rgba(127,235,168,0.06)',
    borderRadius: 10,
    padding: '12px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 4,
  },
  statIcon: {
    fontSize: '1.4rem',
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 800,
    color: '#fff',
    fontVariantNumeric: 'tabular-nums',
  },
  statLabel: {
    fontSize: '0.7rem',
    color: '#aedfc4',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
  },
  // Section 6: Wisdom — compact bottom bar (outside grid)
  wisdom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '10px 24px',
    background: 'rgba(127,235,168,0.04)',
    border: '1px solid rgba(127,235,168,0.12)',
    borderRadius: '10px',
  },
  wisdomIcon: {
    fontSize: '1.2rem',
    flexShrink: 0,
  },
  wisdomTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#7feba8',
    margin: 0,
    fontStyle: 'italic',
  },
  wisdomSep: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: '0.9rem',
  },
  wisdomTranslation: {
    fontSize: '0.9rem',
    color: '#ddd',
    margin: 0,
  },
};

// ── Global Keyframe Animation (injected once) ────────────────────────────────

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes marquee-scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  @keyframes pulse-live {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  body { margin: 0; padding: 0; }
  .reunion-dashboard-active { overflow: hidden; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(127,235,168,0.3); border-radius: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
`;
if (!document.querySelector('[data-reunion-dashboard-styles]')) {
  styleSheet.setAttribute('data-reunion-dashboard-styles', '');
  document.head.appendChild(styleSheet);
}
